
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { GlobeAltIcon, PhotographIcon, PresentationChartBarIcon, DotsHorizontalIcon, CogIcon, } from '@heroicons/react/outline';
import { SelectorIcon } from '@heroicons/react/solid';
import { FaRobot } from 'react-icons/fa';
import { FaPaperclip } from 'react-icons/fa6';
import Image from 'next/image';
import { useState } from 'react';
import Seo from '@/components/Seo';
import AuthModal from '@/components/AuthModal';
import { getPublishedBotByUuid } from '@/data/bots';
import { createClient } from '@/utils/supabase/server-props';
import GoogleAISvg from '~/svg/google.svg';
import SunSVG from '~/svg/Sun.svg';
interface BotPageData {
    publicId: string;
    name: string;
    description: string;
    avatar: string;
    author: string;
}
async function getServerSideProps(context: GetServerSidePropsContext) {
    const uuid = context.params?.uuid as string;
    if (!uuid) {
        return { notFound: true };
    }
    // Fetch user and bot data in parallel
    const supabase = createClient(context);
    const [{ data: { user }, }, bot,] = await Promise.all([supabase.auth.getUser(), getPublishedBotByUuid(uuid)]);
    // If user is authenticated, redirect straight to chat
    if (user) {
        return {
            redirect: {
                destination: `/chat?bot=${uuid}`,
                permanent: false,
            },
        };
    }
    if (!bot) {
        return { notFound: true };
    }
    const botData: BotPageData = {
        publicId: bot.publicId || uuid,
        name: bot.name,
        description: bot.description || '',
        avatar: bot.avatar || '',
        author: bot.bot_author || '',
    };
    return {
        props: { bot: botData },
    };
}
export default function BotPage({ bot, }: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const [showAuthModal, setShowAuthModal] = useState(false);
    const openAuth = () => setShowAuthModal(true);
    const redirectTo = `/chat?bot=${bot.publicId}`;
    return (<>
      <Seo templateTitle={`Chat with ${bot.name}`} description={bot.description ||
            `Chat with ${bot.name} — an AI assistant on Easy-Peasy.AI`} image={bot.avatar || undefined}/>

      {/* Full-screen layout matching the real chat page */}
      <div className='flex h-dvh overflow-hidden sm:h-screen'>
        <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
          <main className='relative flex flex-1 overflow-y-auto bg-gray-100 focus:outline-none dark:bg-slate-900'>
            <div className='relative flex h-dvh min-w-0 flex-1 flex-col overflow-y-auto px-4 md:px-8 mx-auto max-w-4xl xl:max-w-5xl'>
              <div className='relative flex h-dvh w-full flex-col items-stretch overflow-hidden pb-2 pt-2 md:p-4'>
                <div className='flex w-full flex-1 flex-col overflow-y-auto'>
                  {/* Header bar - model selector + settings */}
                  <div className='sticky top-0 z-10 flex w-full items-center justify-end sm:justify-between bg-transparent sm:px-4 py-0 transition-all duration-300 dark:bg-transparent md:px-6'>
                    {/* Model selector */}
                    <div className='flex items-center gap-2'>
                      <button onClick={openAuth} className='group flex sm:max-w-full items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-900 shadow-sm ring-1 ring-gray-200 transition-all hover:bg-gray-50 hover:ring-gray-300 dark:bg-slate-800 dark:text-white dark:ring-white/10 dark:hover:bg-slate-700'>
                        <GoogleAISvg className='mr-1 h-4 w-4 align-text-top'/>
                        <span className='truncate'>Gemini 3 Flash</span>
                        <SelectorIcon className='h-3 w-3 text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'/>
                      </button>
                    </div>

                    {/* Right side icons */}
                    <div className='flex items-center gap-1 md:gap-2'>
                      <button onClick={openAuth} className='flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white' title='Prompt library'>
                        <SunSVG className='h-4 w-4'/>
                      </button>
                      <div className='ml-1 h-4 w-px bg-gray-200 dark:bg-gray-700'/>
                      <button onClick={openAuth} className='flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white' title='Chat settings'>
                        <CogIcon className='h-4 w-4'/>
                      </button>
                    </div>
                  </div>

                  {/* Welcome area - centered vertically */}
                  <div className='flex min-h-[calc(100dvh-120px)] flex-col items-center px-0 sm:px-4'>
                    <div className='flex w-full max-w-3xl flex-1 flex-col justify-center'>
                      {/* Title with bot avatar */}
                      <h1 className={`mb-6 flex flex-col items-center justify-center gap-2 text-center font-serif font-normal sm:mb-8 sm:flex-row sm:gap-3 ${bot.name.length > 30
            ? 'text-lg sm:text-xl md:text-2xl lg:text-3xl'
            : bot.name.length > 20
                ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl'
                : 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl'}`}>
                        {bot.avatar ? (<Image src={bot.avatar} width={40} height={40} alt={bot.name} className='w-10 rounded-full sm:w-12'/>) : (<div className='flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-lg font-semibold text-white sm:h-12 sm:w-12 sm:text-xl'>
                            {bot.name.charAt(0).toUpperCase()}
                          </div>)}
                        <span>Chat with {bot.name}</span>
                      </h1>

                      {/* Chat input area - matching ChatInput exactly */}
                      <div className='isolate w-full dark:border-white/20 md:border-transparent md:pt-0 md:dark:border-transparent'>
                        <div className='m-auto w-full px-0 text-base md:px-5 lg:px-4 xl:px-5'>
                          <div className='mx-auto flex flex-1 flex-col gap-1 text-base md:max-w-3xl lg:max-w-[40rem] xl:max-w-[48rem]'>
                            <div className='w-full'>
                              <div className='relative z-[1] flex h-full max-w-full flex-1 flex-col'>
                                <div className='group relative z-[1] flex w-full items-center'>
                                  <div className='w-full'>
                                    <div className='border-token-border-light flex w-full cursor-text flex-col rounded-3xl border bg-white px-3 py-1 shadow-[0_9px_9px_0px_rgba(0,0,0,0.01),_0_2px_5px_0px_rgba(0,0,0,0.06)] duration-150 ease-in-out dark:border-none dark:bg-slate-800 dark:shadow-none' onClick={openAuth}>
                                      {/* Textarea area */}
                                      <div className='flex min-h-[44px] items-start pl-1'>
                                        <div className='min-w-0 max-w-full flex-1'>
                                          <div className='max-h-52 overflow-auto'>
                                            <textarea readOnly rows={2} placeholder='Send a message...' className='w-full resize-none border-0 bg-transparent px-0 py-2 text-sm focus:ring-0 focus-visible:ring-0 dark:bg-transparent dark:text-white' onFocus={openAuth}/>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Bottom controls */}
                                      <div className='mb-2 mt-1 flex items-center justify-between gap-2 sm:mt-0'>
                                        {/* Left - attachment + bot badge */}
                                        <div className='flex min-w-0 flex-1 items-center gap-1.5'>
                                          {/* Attachment button */}
                                          <button type='button' onClick={(e) => {
            e.stopPropagation();
            openAuth();
        }} className='border-token-border-light hover:bg-token-main-surface-secondary flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border dark:hover:bg-gray-700'>
                                            <FaPaperclip className='h-[18px] w-[18px] -rotate-45 text-gray-500 dark:text-slate-200'/>
                                          </button>

                                          {/* Bot badge */}
                                          <div className='flex h-9 items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 text-sm font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'>
                                            {bot.avatar ? (<Image src={bot.avatar} width={16} height={16} alt={bot.name} className='h-4 w-4 rounded-full object-cover'/>) : (<div className='flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-[8px] font-semibold text-white'>
                                                {bot.name
                .charAt(0)
                .toUpperCase()}
                                              </div>)}
                                            <span className='max-w-[100px] truncate'>
                                              {bot.name}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Right - voice, mic, send */}
                                        <div className='flex flex-shrink-0 items-center gap-1.5'>
                                          {/* Voice/None button */}
                                          <button type='button' onClick={(e) => {
            e.stopPropagation();
            openAuth();
        }} className='border-token-border-light hover:bg-token-main-surface-secondary flex h-9 min-w-9 items-center justify-center gap-1 rounded-full border px-3 dark:hover:bg-gray-700'>
                                            <svg xmlns='http://www.w3.org/2000/svg' className='h-[18px] w-[18px] text-gray-500 dark:text-slate-200' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                                              <path strokeLinecap='round' strokeLinejoin='round' d='M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z'/>
                                            </svg>
                                            <span className='hidden text-[13px] font-medium text-gray-500 dark:text-slate-200 md:block'>
                                              None
                                            </span>
                                            <svg xmlns='http://www.w3.org/2000/svg' className='h-3 w-3 text-gray-400' viewBox='0 0 20 20' fill='currentColor'>
                                              <path fillRule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clipRule='evenodd'/>
                                            </svg>
                                          </button>

                                          {/* Microphone button */}
                                          <button type='button' onClick={(e) => {
            e.stopPropagation();
            openAuth();
        }} className='border-token-border-light hover:bg-token-main-surface-secondary flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border dark:hover:bg-gray-700'>
                                            <svg xmlns='http://www.w3.org/2000/svg' className='h-[18px] w-[18px] text-gray-500 dark:text-slate-200' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                                              <path strokeLinecap='round' strokeLinejoin='round' d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'/>
                                            </svg>
                                          </button>

                                          {/* Send button */}
                                          <button type='button' onClick={(e) => {
            e.stopPropagation();
            openAuth();
        }} className='flex h-9 w-9 items-center justify-center rounded-full bg-black text-white hover:opacity-70 dark:bg-white dark:text-black'>
                                            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' className='h-4 w-4'>
                                              <path d='M7 11L12 6L17 11M12 18V7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Mode buttons row */}
                      <div className='mt-3 sm:mt-4'>
                        <div className='flex flex-wrap items-center justify-center gap-2'>
                          {[
            {
                icon: GlobeAltIcon,
                label: 'Build website',
                active: false,
            },
            {
                icon: PhotographIcon,
                label: 'Images',
                active: false,
            },
            { icon: FaRobot, label: 'Bots', active: true },
            {
                icon: PresentationChartBarIcon,
                label: 'Create slides',
                active: false,
            },
            {
                icon: DotsHorizontalIcon,
                label: 'More',
                active: false,
            },
        ].map(({ icon: Icon, label, active }) => (<button key={label} type='button' onClick={openAuth} className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-all active:scale-95 sm:px-4 sm:py-2.5 ${active
                ? 'border-primary-200 bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-700'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                              <Icon className='h-4 w-4'/>
                              <span className='whitespace-nowrap'>{label}</span>
                            </button>))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal showModal={showAuthModal} onClose={() => setShowAuthModal(false)} redirectTo={redirectTo} botName={bot.name} botAvatar={bot.avatar || undefined}/>
    </>);
}

    async function __Next_Translate__getServerSideProps__1a11b03d325__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/bot/[uuid]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a11b03d325__ as getServerSideProps }
  